import type { ChainModifiers, Entry, EntryFieldTypes, EntrySkeletonType, LocaleCode } from 'contentful'
import type { TypeComponentCodeSkeleton } from './TypeComponentCode'
import type { TypeComponentCodeTabsSkeleton } from './TypeComponentCodeTabs'
import type { TypeComponentCtaLinkSkeleton } from './TypeComponentCtaLink'
import type { TypeComponentHubiloFormSkeleton } from './TypeComponentHubiloForm'
import type { TypeComponentHubspotCtaSkeleton } from './TypeComponentHubspotCta'
import type { TypeComponentHubSpotFormSkeleton } from './TypeComponentHubSpotForm'
import type { TypeComponentIconSkeleton } from './TypeComponentIcon'
import type { TypeComponentLottieSkeleton } from './TypeComponentLottie'
import type { TypeComponentStatSkeleton } from './TypeComponentStat'
import type { TypeComponentTableSkeleton } from './TypeComponentTable'
import type { TypeComponentVideoSkeleton } from './TypeComponentVideo'
import type { TypeResourceImageSkeleton } from './TypeResourceImage'
import type { TypeSectionBarGraphSkeleton } from './TypeSectionBarGraph'

export interface TypeComponentCardFields {
  internalName?: EntryFieldTypes.Symbol
  tagline?: EntryFieldTypes.Symbol
  headline?: EntryFieldTypes.Symbol
  subtext?: EntryFieldTypes.RichText
  subtextAlignment?: EntryFieldTypes.Symbol<'Center' | 'Left' | 'Right'>
  image?: EntryFieldTypes.AssetLink
  imageDarkMode?: EntryFieldTypes.AssetLink
  asset?: EntryFieldTypes.EntryLink<
    | TypeComponentCodeSkeleton
    | TypeComponentCodeTabsSkeleton
    | TypeComponentHubSpotFormSkeleton
    | TypeComponentHubiloFormSkeleton
    | TypeComponentIconSkeleton
    | TypeComponentLottieSkeleton
    | TypeComponentTableSkeleton
    | TypeComponentVideoSkeleton
    | TypeResourceImageSkeleton
    | TypeSectionBarGraphSkeleton
  >
  imageBackgroundColor?: EntryFieldTypes.Symbol<
    'Indigo Blue' | 'Light Gray' | 'Primary Blue (Blend Mode)' | 'Primary Blue' | 'White'
  >
  cardBackgroundColor?: EntryFieldTypes.Symbol<'Indigo Blue' | 'Light Gray' | 'Primary Blue' | 'White'>
  gradientCurves?: EntryFieldTypes.Symbol<'Gradient Curves 1'>
  showHeroImageOnMobile?: EntryFieldTypes.Boolean
  actions?: EntryFieldTypes.Array<
    EntryFieldTypes.EntryLink<TypeComponentCtaLinkSkeleton | TypeComponentHubspotCtaSkeleton>
  >
  filterTags?: EntryFieldTypes.Array<EntryFieldTypes.Symbol>
  headlineIcon?: EntryFieldTypes.EntryLink<TypeComponentIconSkeleton>
  stats?: EntryFieldTypes.Array<EntryFieldTypes.EntryLink<TypeComponentStatSkeleton>>
  centerCardTextAndActions?: EntryFieldTypes.Boolean
}

export type TypeComponentCardSkeleton = EntrySkeletonType<TypeComponentCardFields, 'componentCard'>
export type TypeComponentCard<Modifiers extends ChainModifiers, Locales extends LocaleCode = LocaleCode> = Entry<
  TypeComponentCardSkeleton,
  Modifiers,
  Locales
>

export function isTypeComponentCard<Modifiers extends ChainModifiers, Locales extends LocaleCode>(
  entry?: Entry<EntrySkeletonType, Modifiers, Locales> | undefined
): entry is TypeComponentCard<Modifiers, Locales> {
  return entry?.sys?.contentType?.sys?.id === 'componentCard'
}
